import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";
import {
  CaretLeft,
  CaretRight,
  Check,
  PencilSimple,
  CalendarBlank,
  UserCircle,
  CheckCircle,
  QrCode,
  Funnel,
  Plus,
  X,
  GearSix,
  UploadSimple,
  FileText,
  Trash,
  SpinnerGap,
} from "@phosphor-icons/react";
import {
  AssetMaintenanceSchedule,
  MaintenanceFrequencyLabels,
  MaintenanceType,
  MaintenanceEvent,
  MaintenanceEventFile,
  MaintenanceEventStatus,
  MaintenanceEventStatusLabels,
} from "shared/types/asset";
import { useAssetCategories } from "hooks/useAssets";
import { getIconByValue } from "components/dueDiligence/IconPicker/icon-categories";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isToday,
  isBefore,
} from "date-fns";
import { nl } from "date-fns/locale";
import { Link } from "react-router-dom";
import { MaintenanceEditDialog } from "components/assets/MaintenanceEditDialog";
import { MaintenanceCreateDialog } from "components/assets/MaintenanceCreateDialog";
import { MaintenanceValidationDialog } from "components/assets/MaintenanceValidationDialog";
import { MaintenanceQRSection } from "components/assets/MaintenanceQRSection";
import { useUsers } from "shared/contexts/users.context";

interface MaintenanceCalendarProps {
  schedules: AssetMaintenanceSchedule[];
  events?: MaintenanceEvent[];
  onRefetch: () => void;
  /** When provided, shows a create button scoped to this asset */
  assetId?: string;
}

type ViewMode = "year" | "month";

// Maintenance type color constants
const TYPE_COLORS = {
  critical: "#ef4444", // red-500
  preventive: "#22c55e", // green-500
} as const;

// Calendar item: either a schedule (openstaand) or an event (validated/reported)
type CalendarItem =
  | { type: "schedule"; data: AssetMaintenanceSchedule }
  | { type: "event"; data: MaintenanceEvent };

function getMaintenanceTypeColor(schedule: AssetMaintenanceSchedule): string {
  return schedule.maintenanceType === MaintenanceType.CRITICAL
    ? TYPE_COLORS.critical
    : TYPE_COLORS.preventive;
}

function getItemTypeColor(item: CalendarItem): string {
  if (item.type === "schedule") {
    return getMaintenanceTypeColor(item.data);
  }
  // For events, use the schedule's maintenance type if available
  const schedule = item.data.schedule;
  if (schedule) {
    return getMaintenanceTypeColor(schedule);
  }
  return TYPE_COLORS.preventive;
}

// Build a combined map of date string -> calendar items
function buildDateMap(
  schedules: AssetMaintenanceSchedule[],
  events: MaintenanceEvent[]
) {
  const map = new Map<string, CalendarItem[]>();

  for (const s of schedules) {
    if (!s.nextDueDate) continue;
    const key = format(new Date(s.nextDueDate), "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({ type: "schedule", data: s });
  }

  for (const e of events) {
    if (!e.dueDate) continue;
    const key = format(new Date(e.dueDate), "yyyy-MM-dd");
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push({ type: "event", data: e });
  }

  return map;
}

export const MaintenanceCalendar = ({
  schedules,
  events = [],
  onRefetch,
  assetId,
}: MaintenanceCalendarProps) => {
  const [viewMode, setViewMode] = useState<ViewMode>("year");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());

  // Filter state
  const [filterCategoryId, setFilterCategoryId] = useState<number | null>(null);
  const [filterTypeId, setFilterTypeId] = useState<number | null>(null);
  const [showOpenstaand, setShowOpenstaand] = useState(true);
  const [showGevalideerd, setShowGevalideerd] = useState(true);
  const [showKritiek, setShowKritiek] = useState(true);
  const [showPreventief, setShowPreventief] = useState(true);

  // Fetch all categories/types from backend (always visible, even with no maintenance items)
  const { categories } = useAssetCategories();

  // Count maintenance items per category and type
  const allItems = useMemo(() => [...schedules, ...events.map((e) => ({ asset: e.asset }))], [schedules, events]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<number, number>();
    allItems.forEach((item) => {
      const catId = item.asset?.assetCategoryId;
      if (catId) counts.set(catId, (counts.get(catId) || 0) + 1);
    });
    return counts;
  }, [allItems]);

  const typeCounts = useMemo(() => {
    const counts = new Map<number, number>();
    allItems.forEach((item) => {
      const typeId = item.asset?.assetTypeId;
      if (typeId) counts.set(typeId, (counts.get(typeId) || 0) + 1);
    });
    return counts;
  }, [allItems]);

  const totalItemCount = schedules.length + events.length;

  // Active category's types
  const activeCategory = categories.find((c) => c.id === filterCategoryId);
  const categoryTypes = useMemo(() => {
    if (!activeCategory) return [];
    return (activeCategory.assetTypes || [])
      .filter((t) => t.active)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [activeCategory]);

  // Apply filters to schedules and events
  const filteredSchedules = useMemo(() => {
    if (!showOpenstaand) return [];
    return schedules.filter((s) => {
      if (filterCategoryId && s.asset?.assetCategoryId !== filterCategoryId) return false;
      if (filterTypeId && s.asset?.assetTypeId !== filterTypeId) return false;
      if (!showKritiek && s.maintenanceType === MaintenanceType.CRITICAL) return false;
      if (!showPreventief && s.maintenanceType !== MaintenanceType.CRITICAL) return false;
      return true;
    });
  }, [schedules, filterCategoryId, filterTypeId, showOpenstaand, showKritiek, showPreventief]);

  const filteredEvents = useMemo(() => {
    if (!showGevalideerd) return [];
    return events.filter((e) => {
      if (filterCategoryId && e.asset?.assetCategoryId !== filterCategoryId) return false;
      if (filterTypeId && e.asset?.assetTypeId !== filterTypeId) return false;
      const mt = e.schedule?.maintenanceType;
      if (!showKritiek && mt === MaintenanceType.CRITICAL) return false;
      if (!showPreventief && mt !== MaintenanceType.CRITICAL) return false;
      return true;
    });
  }, [events, filterCategoryId, filterTypeId, showGevalideerd, showKritiek, showPreventief]);

  const hasActiveFilters = filterCategoryId !== null || filterTypeId !== null || !showOpenstaand || !showGevalideerd || !showKritiek || !showPreventief;

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  // Edit dialog state
  const [editingSchedule, setEditingSchedule] =
    useState<AssetMaintenanceSchedule | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // Validation dialog state
  const [validatingSchedule, setValidatingSchedule] =
    useState<AssetMaintenanceSchedule | null>(null);

  const handleEdit = (schedule: AssetMaintenanceSchedule) => {
    setEditingSchedule(schedule);
    setEditDialogOpen(true);
  };

  const handleValidateClick = (schedule: AssetMaintenanceSchedule) => {
    setValidatingSchedule(schedule);
  };

  const dateMap = useMemo(() => buildDateMap(filteredSchedules, filteredEvents), [filteredSchedules, filteredEvents]);

  // Get items for a specific month
  const getMonthItems = (year: number, month: number) => {
    const result: CalendarItem[] = [];
    const monthStr = `${year}-${String(month + 1).padStart(2, "0")}`;
    for (const [key, items] of dateMap) {
      if (key.startsWith(monthStr)) {
        result.push(...items);
      }
    }
    return result;
  };

  // Get items for a specific day
  const getDayItems = (date: Date) => {
    const key = format(date, "yyyy-MM-dd");
    return dateMap.get(key) || [];
  };

  return (
    <div className="pb-24 space-y-4">
      {/* Create button + Filters row */}
      <div className="flex flex-wrap items-center gap-2">
        {assetId && (
          <>
            <Button
              size="sm"
              onClick={() => setCreateDialogOpen(true)}
            >
              <Plus size={14} className="mr-1" />
              Toevoegen
            </Button>
            <div className="w-px h-5 bg-border mx-0.5" />
          </>
        )}
        <Funnel size={14} className="text-muted-foreground shrink-0" />

        {/* Status toggles */}
        <button
          onClick={() => setShowOpenstaand((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition-colors ${
            showOpenstaand
              ? "bg-amber-50 border-amber-200 text-amber-800"
              : "bg-muted/30 border-border/60 text-muted-foreground line-through"
          }`}
        >
          <CalendarBlank size={12} />
          Openstaand
        </button>
        <button
          onClick={() => setShowGevalideerd((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition-colors ${
            showGevalideerd
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-muted/30 border-border/60 text-muted-foreground line-through"
          }`}
        >
          <CheckCircle size={12} />
          Gevalideerd
        </button>

        <div className="w-px h-5 bg-border mx-0.5" />

        {/* Maintenance type toggles */}
        <button
          onClick={() => setShowKritiek((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition-colors ${
            showKritiek
              ? "bg-red-50 border-red-200 text-red-800"
              : "bg-muted/30 border-border/60 text-muted-foreground line-through"
          }`}
        >
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: showKritiek ? TYPE_COLORS.critical : "#a1a1aa" }} />
          Kritiek
        </button>
        <button
          onClick={() => setShowPreventief((v) => !v)}
          className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border text-xs transition-colors ${
            showPreventief
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-muted/30 border-border/60 text-muted-foreground line-through"
          }`}
        >
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: showPreventief ? TYPE_COLORS.preventive : "#a1a1aa" }} />
          Preventief
        </button>

        {hasActiveFilters && (
          <div className="w-px h-5 bg-border mx-0.5" />
        )}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs gap-1"
            onClick={() => {
              setFilterCategoryId(null);
              setFilterTypeId(null);
              setShowOpenstaand(true);
              setShowGevalideerd(true);
              setShowKritiek(true);
              setShowPreventief(true);
            }}
          >
            <X size={12} />
            Wis filters
          </Button>
        )}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => { setFilterCategoryId(null); setFilterTypeId(null); }}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            filterCategoryId === null
              ? "bg-primary text-primary-foreground border-primary"
              : "bg-background text-muted-foreground border-border hover:bg-muted"
          }`}
        >
          Alle ({totalItemCount})
        </button>
        {categories.filter((c) => c.active).sort((a, b) => a.name.localeCompare(b.name)).map((cat) => {
          const isActive = filterCategoryId === cat.id;
          const count = categoryCounts.get(cat.id) || 0;
          return (
            <div key={cat.id} className="flex items-center gap-0.5">
              <button
                onClick={() => {
                  setFilterCategoryId(isActive ? null : cat.id);
                  setFilterTypeId(null);
                }}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                {cat.name} ({count})
              </button>
              {isActive && (
                <Link
                  to={`/voorzieningen/categorieen/${cat.id}`}
                  className="rounded-full border border-border p-1 hover:bg-muted transition-colors"
                  title="Categorie bewerken"
                >
                  <GearSix size={12} className="text-muted-foreground" />
                </Link>
              )}
            </div>
          );
        })}
      </div>

      {/* Type sub-filter chips */}
      {filterCategoryId !== null && categoryTypes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setFilterTypeId(null)}
            className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
              filterTypeId === null
                ? "bg-moss-primary/15 text-moss-primary border-moss-primary/30"
                : "bg-background text-muted-foreground border-border hover:bg-muted"
            }`}
          >
            Alle types
          </button>
          {categoryTypes.map((type) => {
            const isActive = filterTypeId === type.id;
            const TypeIcon = type.icon ? getIconByValue(type.icon) : null;
            const count = typeCounts.get(type.id) || 0;
            return (
              <button
                key={type.id}
                onClick={() => setFilterTypeId(isActive ? null : type.id)}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  isActive
                    ? "bg-moss-primary/15 text-moss-primary border-moss-primary/30"
                    : "bg-background text-muted-foreground border-border hover:bg-muted"
                }`}
              >
                {TypeIcon && <TypeIcon size={14} weight="duotone" />}
                {type.name} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* View toggle + navigation */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === "year" ? "default" : "outline"}
            size="sm"
            onClick={() => setViewMode("year")}
          >
            Jaaroverzicht
          </Button>
          <Button
            variant={viewMode === "month" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              if (viewMode !== "month") {
                setSelectedMonth(new Date().getMonth());
                setSelectedYear(new Date().getFullYear());
              }
              setViewMode("month");
            }}
          >
            Maandoverzicht
          </Button>
        </div>

        {viewMode === "year" ? (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedYear((y) => y - 1)}
            >
              <CaretLeft size={16} />
            </Button>
            <span className="font-semibold text-sm min-w-[60px] text-center">
              {selectedYear}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedYear((y) => y + 1)}
            >
              <CaretRight size={16} />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear((y) => y - 1);
                } else {
                  setSelectedMonth((m) => m - 1);
                }
              }}
            >
              <CaretLeft size={16} />
            </Button>
            <span className="font-semibold text-sm min-w-[140px] text-center capitalize">
              {format(new Date(selectedYear, selectedMonth), "MMMM yyyy", {
                locale: nl,
              })}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear((y) => y + 1);
                } else {
                  setSelectedMonth((m) => m + 1);
                }
              }}
            >
              <CaretRight size={16} />
            </Button>
          </div>
        )}
      </div>

      {/* Year view */}
      {viewMode === "year" && (
        <div className="space-y-6">
          <YearView
            year={selectedYear}
            getMonthItems={getMonthItems}
            onMonthClick={(month) => {
              setSelectedMonth(month);
              setViewMode("month");
            }}
            onMonthCalendarClick={(month) => {
              setSelectedMonth(month);
              setViewMode("month");
            }}
          />
          <YearList
            schedules={filteredSchedules}
            events={filteredEvents}
            year={selectedYear}
            onValidateClick={handleValidateClick}
            onEdit={handleEdit}
            onRefetch={onRefetch}
          />
        </div>
      )}

      {/* Month view: calendar + list */}
      {viewMode === "month" && (
        <div className="space-y-6">
          <MonthView
            year={selectedYear}
            month={selectedMonth}
            getDayItems={getDayItems}
            onValidateClick={handleValidateClick}
            onEdit={handleEdit}
          />
          <MonthListView
            items={getMonthItems(selectedYear, selectedMonth)}
            onValidateClick={handleValidateClick}
            onEdit={handleEdit}
            onRefetch={onRefetch}
          />
        </div>
      )}

      {/* Create dialog */}
      {assetId && (
        <MaintenanceCreateDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          onCreated={onRefetch}
          assetId={assetId}
        />
      )}

      {/* Edit dialog */}
      <MaintenanceEditDialog
        schedule={editingSchedule}
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        onSaved={onRefetch}
      />

      {/* Validation dialog */}
      <MaintenanceValidationDialog
        schedule={validatingSchedule}
        onClose={() => setValidatingSchedule(null)}
        onSuccess={onRefetch}
      />
    </div>
  );
};

// ==================== Year View ====================

interface YearViewProps {
  year: number;
  getMonthItems: (year: number, month: number) => CalendarItem[];
  onMonthClick: (month: number) => void;
  onMonthCalendarClick: (month: number) => void;
}

const MONTH_NAMES_NL = [
  "Januari", "Februari", "Maart", "April", "Mei", "Juni",
  "Juli", "Augustus", "September", "Oktober", "November", "December",
];

function YearView({
  year,
  getMonthItems,
  onMonthClick,
  onMonthCalendarClick,
}: YearViewProps) {
  const now = new Date();

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
      {MONTH_NAMES_NL.map((name, monthIdx) => {
        const monthItems = getMonthItems(year, monthIdx);
        const openSchedules = monthItems.filter((i) => i.type === "schedule");
        const validatedEvents = monthItems.filter(
          (i) => i.type === "event" && i.data.status === MaintenanceEventStatus.VALIDATED
        );
        const reportedEvents = monthItems.filter(
          (i) => i.type === "event" && i.data.status === MaintenanceEventStatus.REPORTED
        );

        const overdueCount = openSchedules.filter(
          (i) => i.data.nextDueDate && isBefore(new Date(i.data.nextDueDate), now)
        ).length;

        const criticalCount = openSchedules.filter(
          (i) => (i.data as AssetMaintenanceSchedule).maintenanceType === MaintenanceType.CRITICAL
        ).length;
        const preventiveCount = openSchedules.filter(
          (i) => (i.data as AssetMaintenanceSchedule).maintenanceType !== MaintenanceType.CRITICAL
        ).length;

        const isCurrentMonth =
          year === now.getFullYear() && monthIdx === now.getMonth();

        return (
          <div
            key={monthIdx}
            className={`rounded-lg border bg-card p-3 text-left transition-all hover:shadow-sm ${
              isCurrentMonth ? "border-primary/30 ring-1 ring-primary/10" : "border-border/60"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <button
                className="text-sm font-medium hover:text-primary transition-colors"
                onClick={() => onMonthClick(monthIdx)}
              >
                {name}
              </button>
              <div className="flex items-center gap-1.5">
                {monthItems.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {monthItems.length}
                  </span>
                )}
                <button
                  onClick={() => onMonthCalendarClick(monthIdx)}
                  className="p-0.5 rounded hover:bg-accent text-muted-foreground hover:text-primary transition-colors"
                  title="Kalenderweergave"
                >
                  <CalendarBlank size={14} />
                </button>
              </div>
            </div>
            {monthItems.length > 0 ? (
              <button
                className="w-full text-left"
                onClick={() => onMonthClick(monthIdx)}
              >
                <div className="space-y-1">
                  <div className="flex flex-wrap gap-2">
                    {criticalCount > 0 && (
                      <div className="flex items-center gap-0.5">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: TYPE_COLORS.critical }}
                        />
                        <span className="text-[10px] text-muted-foreground">
                          {criticalCount}
                        </span>
                      </div>
                    )}
                    {preventiveCount > 0 && (
                      <div className="flex items-center gap-0.5">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: TYPE_COLORS.preventive }}
                        />
                        <span className="text-[10px] text-muted-foreground">
                          {preventiveCount}
                        </span>
                      </div>
                    )}
                    {validatedEvents.length > 0 && (
                      <div className="flex items-center gap-0.5">
                        <CheckCircle size={12} className="text-green-600" />
                        <span className="text-[10px] text-muted-foreground">
                          {validatedEvents.length}
                        </span>
                      </div>
                    )}
                    {reportedEvents.length > 0 && (
                      <div className="flex items-center gap-0.5">
                        <UserCircle size={12} className="text-blue-600" />
                        <span className="text-[10px] text-muted-foreground">
                          {reportedEvents.length}
                        </span>
                      </div>
                    )}
                  </div>
                  {overdueCount > 0 && (
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-[10px] text-red-600 font-medium">
                        {overdueCount} achterstallig
                      </span>
                    </div>
                  )}
                </div>
              </button>
            ) : (
              <p className="text-[10px] text-muted-foreground">
                Geen onderhoud
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ==================== Upcoming List (below year view) ====================

interface YearListProps {
  schedules: AssetMaintenanceSchedule[];
  events: MaintenanceEvent[];
  year: number;
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
  onRefetch?: () => void;
}

function YearList({ schedules, events, year, onValidateClick, onEdit, onRefetch }: YearListProps) {
  const now = new Date();
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year + 1, 0, 1);

  const filteredSchedules = useMemo(() => {
    return [...schedules]
      .filter((s) => {
        if (!s.active || !s.nextDueDate) return false;
        const due = new Date(s.nextDueDate);
        return due >= yearStart && due < yearEnd;
      })
      .sort((a, b) => new Date(a.nextDueDate!).getTime() - new Date(b.nextDueDate!).getTime());
  }, [schedules, year]);

  const { reportedEvents, validatedEvents } = useMemo(() => {
    const inYear = events.filter((e) => {
      if (!e.dueDate) return false;
      const due = new Date(e.dueDate);
      return due >= yearStart && due < yearEnd;
    });
    return {
      reportedEvents: inYear
        .filter((e) => e.status === MaintenanceEventStatus.REPORTED)
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
      validatedEvents: inYear
        .filter((e) => e.status === MaintenanceEventStatus.VALIDATED)
        .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
    };
  }, [events, year]);

  const totalCount = filteredSchedules.length + reportedEvents.length + validatedEvents.length;
  if (totalCount === 0) return null;

  return (
    <div className="space-y-4">
      {/* Open schedules */}
      {filteredSchedules.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CalendarBlank size={14} className="text-muted-foreground" />
            <span className="text-sm font-semibold">
              Openstaand ({filteredSchedules.length})
            </span>
          </div>
          <div className="space-y-2">
            {filteredSchedules.map((s) => (
              <ScheduleListItem
                key={s.id}
                schedule={s}
                now={now}
                onValidateClick={onValidateClick}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Reported by supplier */}
      {reportedEvents.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <UserCircle size={14} className="text-blue-600" />
            <span className="text-sm font-semibold">
              Gerapporteerd door leverancier ({reportedEvents.length})
            </span>
          </div>
          <div className="space-y-2">
            {reportedEvents.map((e) => (
              <EventListItem key={e.id} event={e} onRefetch={onRefetch} />
            ))}
          </div>
        </div>
      )}

      {/* Validated */}
      {validatedEvents.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle size={14} className="text-green-600" />
            <span className="text-sm font-semibold">
              Gevalideerd ({validatedEvents.length})
            </span>
          </div>
          <div className="space-y-2">
            {validatedEvents.map((e) => (
              <EventListItem key={e.id} event={e} onRefetch={onRefetch} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Month List View ====================

interface MonthListViewProps {
  items: CalendarItem[];
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
  onRefetch?: () => void;
}

function MonthListView({ items, onValidateClick, onEdit, onRefetch }: MonthListViewProps) {
  const openSchedules = items.filter((i) => i.type === "schedule").map((i) => i.data as AssetMaintenanceSchedule);
  const reportedEvents = items.filter(
    (i) => i.type === "event" && i.data.status === MaintenanceEventStatus.REPORTED
  ).map((i) => i.data as MaintenanceEvent);
  const validatedEvents = items.filter(
    (i) => i.type === "event" && i.data.status === MaintenanceEventStatus.VALIDATED
  ).map((i) => i.data as MaintenanceEvent);

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <CalendarBlank size={32} className="mx-auto mb-2 opacity-50" />
        <p className="text-sm">Geen onderhoud voor deze maand</p>
      </div>
    );
  }

  const now = new Date();

  return (
    <div className="space-y-4">
      {/* Open schedules */}
      {openSchedules.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CalendarBlank size={14} className="text-muted-foreground" />
            <span className="text-sm font-semibold">
              Openstaand ({openSchedules.length})
            </span>
          </div>
          <div className="space-y-2">
            {openSchedules.map((s) => (
              <ScheduleListItem
                key={s.id}
                schedule={s}
                now={now}
                onValidateClick={onValidateClick}
                onEdit={onEdit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Reported by supplier */}
      {reportedEvents.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <UserCircle size={14} className="text-blue-600" />
            <span className="text-sm font-semibold">
              Gerapporteerd door leverancier ({reportedEvents.length})
            </span>
          </div>
          <div className="space-y-2">
            {reportedEvents.map((e) => (
              <EventListItem key={e.id} event={e} onRefetch={onRefetch} />
            ))}
          </div>
        </div>
      )}

      {/* Validated */}
      {validatedEvents.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <CheckCircle size={14} className="text-green-600" />
            <span className="text-sm font-semibold">
              Gevalideerd ({validatedEvents.length})
            </span>
          </div>
          <div className="space-y-2">
            {validatedEvents.map((e) => (
              <EventListItem key={e.id} event={e} onRefetch={onRefetch} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== Schedule List Item (Openstaand) ====================

interface ScheduleListItemProps {
  schedule: AssetMaintenanceSchedule;
  now: Date;
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
}

function ScheduleListItem({
  schedule: s,
  now,
  onValidateClick,
  onEdit,
}: ScheduleListItemProps) {
  const [showQR, setShowQR] = useState(false);
  const overdue = s.nextDueDate && isBefore(new Date(s.nextDueDate), now);
  const typeColor = getMaintenanceTypeColor(s);

  return (
    <div className="rounded-lg border border-border/60 bg-card shadow-sm">
      <div className="flex items-start justify-between gap-3 p-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <div
              className="w-2 h-2 rounded-full shrink-0"
              style={{ backgroundColor: typeColor }}
            />
            {s.asset && (
              <Link
                to={`/asset/${s.asset.id}`}
                className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
              >
                {s.asset.name || s.asset.id}
              </Link>
            )}
            {s.asset && <span className="text-muted-foreground">·</span>}
            <span className="text-sm font-medium">{s.name}</span>
            {s.asset?.building && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {s.asset.building.id}
              </Badge>
            )}
            {s.asset?.unit && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {s.asset.unit.id}
              </Badge>
            )}
            {s.asset?.room && (
              <Badge variant="outline" className="text-[10px] px-1 py-0">
                {s.asset.room.name || s.asset.room.id}
              </Badge>
            )}
            {overdue && (
              <Badge variant="destructive" className="text-[10px] shrink-0">
                Achterstallig
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            {s.nextDueDate && (
              <span>{format(new Date(s.nextDueDate), "d MMM yyyy", { locale: nl })}</span>
            )}
            <span>{MaintenanceFrequencyLabels[s.frequency]}</span>
            {s.supplier && <span>- {s.supplier.name}</span>}
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          {s.publicToken && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowQR((v) => !v)}
              className={`h-8 px-2 ${showQR ? "text-primary" : ""}`}
              title="QR code voor leverancier"
            >
              <QrCode size={14} />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(s)}
            className="h-8 px-2"
            title="Bewerk"
          >
            <PencilSimple size={14} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onValidateClick(s)}
            className="h-8 text-xs"
          >
            <Check size={14} className="mr-1" />
            Valideer
          </Button>
        </div>
      </div>
      {showQR && s.publicToken && (
        <div className="border-t px-3 pb-3 pt-2">
          <MaintenanceQRSection
            publicToken={s.publicToken}
            scheduleName={s.name}
            assetName={s.asset?.name}
            compact
          />
        </div>
      )}
    </div>
  );
}

// ==================== Event List Item (Gevalideerd / Gerapporteerd) ====================

interface EventListItemProps {
  event: MaintenanceEvent;
  onRefetch?: () => void;
}

function EventListItem({ event: e, onRefetch }: EventListItemProps) {
  const { session } = useSession();
  const { users } = useUsers();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState<MaintenanceEventFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState<number | null>(null);
  const schedule = e.schedule;
  const typeColor = schedule ? getMaintenanceTypeColor(schedule) : TYPE_COLORS.preventive;
  const validatedByUser = e.validatedBy ? users.find((u: any) => u.id === e.validatedBy) : null;

  // Legacy single-file support
  const hasLegacyFile = !!(e.sharePointFileUrl || e.reportFilePath);

  const fetchFiles = useCallback(async () => {
    if (!session) return;
    setLoadingFiles(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${e.id}/files`,
        { method: 'GET' },
        session,
      );
      if (res.ok) {
        setFiles(await res.json());
      }
    } catch (err) {
      // Silently fail — files section is supplementary
    } finally {
      setLoadingFiles(false);
    }
  }, [e.id, session]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  const handleUploadFile = useCallback(async (file: File) => {
    if (!session) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('reportFile', file);

      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${e.id}/files`,
        { method: 'POST', body: formData },
        session,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Upload mislukt');
      }

      toast.success('Bestand succesvol geüpload');
      await fetchFiles();
      onRefetch?.();
    } catch (err: any) {
      toast.error(err.message || 'Er is een fout opgetreden bij het uploaden');
    } finally {
      setUploading(false);
    }
  }, [e.id, session, fetchFiles, onRefetch]);

  const handleDeleteFile = useCallback(async (fileLogId: number) => {
    if (!session) return;
    setDeletingFileId(fileLogId);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/maintenance-events/${e.id}/files/${fileLogId}`,
        { method: 'DELETE' },
        session,
      );

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.message || 'Verwijderen mislukt');
      }

      toast.success('Bestand verwijderd');
      await fetchFiles();
      onRefetch?.();
    } catch (err: any) {
      toast.error(err.message || 'Er is een fout opgetreden bij het verwijderen');
    } finally {
      setDeletingFileId(null);
    }
  }, [e.id, session, fetchFiles, onRefetch]);

  return (
    <div className="flex items-start justify-between gap-3 rounded-lg border border-border/60 bg-card shadow-sm p-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-1">
          <div
            className="w-2 h-2 rounded-full shrink-0 opacity-60"
            style={{ backgroundColor: typeColor }}
          />
          {e.status === MaintenanceEventStatus.VALIDATED && (
            <CheckCircle size={14} className="text-green-600 shrink-0" />
          )}
          {e.status === MaintenanceEventStatus.REPORTED && (
            <UserCircle size={14} className="text-blue-600 shrink-0" />
          )}
          {e.asset && (
            <Link
              to={`/asset/${e.asset.id}`}
              className="text-sm font-semibold text-foreground hover:text-primary hover:underline"
            >
              {e.asset.name || e.asset.id}
            </Link>
          )}
          {e.asset && <span className="text-muted-foreground">·</span>}
          <Link
            to={`/maintenance-event/${e.id}`}
            className="text-sm font-medium text-foreground hover:text-primary hover:underline"
          >
            {schedule?.name || `Event #${e.id}`}
          </Link>
          {e.asset?.building && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {e.asset.building.id}
            </Badge>
          )}
          {e.asset?.unit && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {e.asset.unit.id}
            </Badge>
          )}
          {e.asset?.room && (
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {e.asset.room.name || e.asset.room.id}
            </Badge>
          )}
          <Badge
            variant={e.status === MaintenanceEventStatus.VALIDATED ? "default" : "secondary"}
            className="text-[10px] shrink-0"
          >
            {MaintenanceEventStatusLabels[e.status]}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{format(new Date(e.dueDate), "d MMM yyyy", { locale: nl })}</span>
          {e.completedDate && (
            <span>Gevalideerd: {format(new Date(e.completedDate), "d MMM yyyy", { locale: nl })}</span>
          )}
          {validatedByUser && (
            <span className="inline-flex items-center gap-1">
              {validatedByUser.image_url ? (
                <img
                  src={validatedByUser.image_url}
                  alt={`${validatedByUser.first_name || ''} ${validatedByUser.last_name || ''}`}
                  className="w-4 h-4 rounded-full object-cover inline-block"
                />
              ) : (
                <UserCircle size={14} className="text-muted-foreground" />
              )}
              {`${validatedByUser.first_name || ''} ${validatedByUser.last_name || ''}`.trim()}
            </span>
          )}
          {e.reporterName && <span>door {e.reporterName}</span>}
        </div>
        {e.summary && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.summary}</p>
        )}
        {e.supplierReport && (
          <p className="text-xs text-blue-600 mt-1 line-clamp-2">{e.supplierReport}</p>
        )}

        {/* File list: legacy + new multi-file */}
        <div className="flex flex-col gap-1 mt-1.5">
          {/* Legacy file (from old single-file column) */}
          {hasLegacyFile && files.length === 0 && (
            <a
              href={e.sharePointFileUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 hover:underline w-fit"
            >
              <FileText size={12} />
              Rapport bekijken
            </a>
          )}

          {/* New multi-file list */}
          {loadingFiles && files.length === 0 && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <SpinnerGap size={12} className="animate-spin" />
              Bestanden laden...
            </div>
          )}
          {files.map((f) => {
            const fileUrl = f.fileLog?.sharePointFileUrl;
            const fileName = f.description || f.fileLog?.summary || `Bestand #${f.fileLogId}`;
            return (
              <div key={f.id} className="flex items-center gap-1.5 group">
                <FileText size={12} className="text-sky-600 shrink-0" />
                {fileUrl ? (
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-sky-600 hover:text-sky-800 hover:underline truncate max-w-[200px]"
                    title={fileName}
                  >
                    {fileName}
                  </a>
                ) : (
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={fileName}>
                    {fileName}
                  </span>
                )}
                <button
                  type="button"
                  className="text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                  disabled={deletingFileId === f.fileLogId}
                  onClick={() => handleDeleteFile(f.fileLogId)}
                  title="Verwijder bestand"
                >
                  {deletingFileId === f.fileLogId ? (
                    <SpinnerGap size={12} className="animate-spin" />
                  ) : (
                    <Trash size={12} />
                  )}
                </button>
              </div>
            );
          })}

          {/* Upload button */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            onChange={(ev) => {
              const file = ev.target.files?.[0];
              if (file) handleUploadFile(file);
              ev.target.value = '';
            }}
          />
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground w-fit"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            <UploadSimple size={12} className="mr-1" />
            {uploading ? 'Uploaden...' : 'Rapport uploaden'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ==================== Month View ====================

interface MonthViewProps {
  year: number;
  month: number;
  getDayItems: (date: Date) => CalendarItem[];
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
}

const WEEKDAY_NAMES = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];

function MonthView({
  year,
  month,
  getDayItems,
  onValidateClick,
  onEdit,
}: MonthViewProps) {
  const monthStart = startOfMonth(new Date(year, month));
  const monthEnd = endOfMonth(monthStart);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });

  const weeks: Date[][] = [];
  let day = calStart;
  while (day <= calEnd) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(day);
      day = addDays(day, 1);
    }
    weeks.push(week);
  }

  const now = new Date();

  return (
    <div className="border border-border/60 rounded-lg overflow-hidden bg-card shadow-sm">
      <div className="grid grid-cols-7 bg-muted/30">
        {WEEKDAY_NAMES.map((d) => (
          <div
            key={d}
            className="text-center text-xs font-medium text-muted-foreground py-2"
          >
            {d}
          </div>
        ))}
      </div>

      {weeks.map((week, weekIdx) => (
        <div key={weekIdx} className="grid grid-cols-7 border-t">
          {week.map((date, dayIdx) => {
            const dayItems = getDayItems(date);
            const inMonth = isSameMonth(date, monthStart);
            const today = isToday(date);

            return (
              <DayCell
                key={dayIdx}
                date={date}
                items={dayItems}
                inMonth={inMonth}
                isToday={today}
                now={now}
                onValidateClick={onValidateClick}
                onEdit={onEdit}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ==================== Day Cell ====================

interface DayCellProps {
  date: Date;
  items: CalendarItem[];
  inMonth: boolean;
  isToday: boolean;
  now: Date;
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
}

function DayCell({
  date,
  items,
  inMonth,
  isToday: todayFlag,
  now,
  onValidateClick,
  onEdit,
}: DayCellProps) {
  if (items.length === 0) {
    return (
      <div
        className={`min-h-[80px] p-1 ${!inMonth ? "bg-muted/20" : ""} ${
          todayFlag ? "bg-blue-50/50 ring-1 ring-inset ring-blue-200" : ""
        }`}
      >
        <span
          className={`text-xs ${
            inMonth ? "text-foreground" : "text-muted-foreground/50"
          } ${todayFlag ? "font-bold text-blue-600" : ""}`}
        >
          {date.getDate()}
        </span>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`min-h-[80px] p-1 text-left w-full hover:bg-accent/30 transition-colors ${
            !inMonth ? "bg-muted/20" : ""
          } ${todayFlag ? "bg-blue-50/50 ring-1 ring-inset ring-blue-200" : ""}`}
        >
          <span
            className={`text-xs ${
              inMonth ? "text-foreground" : "text-muted-foreground/50"
            } ${todayFlag ? "font-bold text-blue-600" : ""}`}
          >
            {date.getDate()}
          </span>
          <div className="flex flex-wrap gap-0.5 mt-1">
            {items.slice(0, 4).map((item, idx) => {
              const color = getItemTypeColor(item);
              const isEvent = item.type === "event";
              const overdue =
                item.type === "schedule" &&
                item.data.nextDueDate &&
                isBefore(new Date(item.data.nextDueDate), now);

              return (
                <div key={idx} className="relative">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      overdue ? "ring-1 ring-red-400 ring-offset-1" : ""
                    } ${isEvent ? "opacity-60" : ""}`}
                    style={{
                      backgroundColor: color,
                      ...(isEvent ? { border: "1px solid currentColor" } : {}),
                    }}
                  />
                  {isEvent && (item.data as MaintenanceEvent).status === MaintenanceEventStatus.VALIDATED && (
                    <CheckCircle
                      size={8}
                      className="absolute -top-0.5 -right-0.5 text-green-600"
                      weight="fill"
                    />
                  )}
                </div>
              );
            })}
            {items.length > 4 && (
              <span className="text-[9px] text-muted-foreground">
                +{items.length - 4}
              </span>
            )}
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <p className="text-sm font-medium">
            {format(date, "d MMMM yyyy", { locale: nl })}
          </p>
          <p className="text-xs text-muted-foreground">
            {items.length} item{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <PopoverItemList
          items={items}
          now={now}
          onValidateClick={onValidateClick}
          onEdit={onEdit}
        />
      </PopoverContent>
    </Popover>
  );
}

// ==================== Popover Item List ====================

interface PopoverItemListProps {
  items: CalendarItem[];
  now: Date;
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
}

function PopoverItemList({
  items,
  now,
  onValidateClick,
  onEdit,
}: PopoverItemListProps) {
  const openSchedules = items.filter((i) => i.type === "schedule");
  const eventItems = items.filter((i) => i.type === "event");

  return (
    <div className="max-h-[300px] overflow-y-auto">
      {openSchedules.length > 0 && (
        <div>
          <div className="px-3 py-1.5 bg-muted/40 border-b">
            <span className="text-xs font-semibold text-muted-foreground">
              Openstaand ({openSchedules.length})
            </span>
          </div>
          {openSchedules.map((item) => (
            <PopoverScheduleItem
              key={(item.data as AssetMaintenanceSchedule).id}
              schedule={item.data as AssetMaintenanceSchedule}
              now={now}
              onValidateClick={onValidateClick}
              onEdit={onEdit}
            />
          ))}
        </div>
      )}
      {eventItems.length > 0 && (
        <div>
          <div className="px-3 py-1.5 bg-muted/40 border-b">
            <span className="text-xs font-semibold text-green-700">
              Afgerond ({eventItems.length})
            </span>
          </div>
          {eventItems.map((item) => (
            <PopoverEventItem
              key={(item.data as MaintenanceEvent).id}
              event={item.data as MaintenanceEvent}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ==================== Popover Schedule Item ====================

interface PopoverScheduleItemProps {
  schedule: AssetMaintenanceSchedule;
  now: Date;
  onValidateClick: (schedule: AssetMaintenanceSchedule) => void;
  onEdit: (schedule: AssetMaintenanceSchedule) => void;
}

function PopoverScheduleItem({
  schedule: s,
  now,
  onValidateClick,
  onEdit,
}: PopoverScheduleItemProps) {
  const [showQR, setShowQR] = useState(false);
  const overdue = s.nextDueDate && isBefore(new Date(s.nextDueDate), now);
  const typeColor = getMaintenanceTypeColor(s);

  return (
    <div className="border-b last:border-b-0">
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-1">
              <div
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: typeColor }}
              />
              <span className="text-sm font-medium truncate">{s.name}</span>
            </div>
            {s.asset && (
              <Link
                to={`/asset/${s.asset.id}`}
                className="text-xs text-primary hover:underline"
              >
                {s.asset.name || s.asset.id}
              </Link>
            )}
            <div className="text-[10px] text-muted-foreground mt-1">
              {MaintenanceFrequencyLabels[s.frequency]}
              {s.supplier ? ` - ${s.supplier.name}` : ""}
            </div>
            {overdue && (
              <Badge variant="destructive" className="text-[10px] mt-1">
                Achterstallig
              </Badge>
            )}
          </div>
          <div className="flex gap-1 shrink-0">
            {s.publicToken && (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowQR((v) => !v);
                }}
                className={`text-xs h-7 px-2 ${showQR ? "text-primary" : ""}`}
                title="QR code"
              >
                <QrCode size={12} />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(s);
              }}
              className="text-xs h-7 px-2"
              title="Bewerk"
            >
              <PencilSimple size={12} />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onValidateClick(s);
              }}
              className="text-xs h-7"
            >
              <Check size={12} className="mr-1" />
              Valideer
            </Button>
          </div>
        </div>
      </div>
      {showQR && s.publicToken && (
        <div className="border-t px-3 pb-3 pt-2">
          <MaintenanceQRSection
            publicToken={s.publicToken}
            scheduleName={s.name}
            assetName={s.asset?.name}
            compact
          />
        </div>
      )}
    </div>
  );
}

// ==================== Popover Event Item ====================

interface PopoverEventItemProps {
  event: MaintenanceEvent;
}

function PopoverEventItem({ event: e }: PopoverEventItemProps) {
  return (
    <div className="p-3 border-b last:border-b-0">
      <div className="flex items-center gap-1.5 mb-1">
        {e.status === MaintenanceEventStatus.VALIDATED ? (
          <CheckCircle size={14} className="text-green-600 shrink-0" />
        ) : (
          <UserCircle size={14} className="text-blue-600 shrink-0" />
        )}
        <span className="text-sm font-medium truncate">
          {e.schedule?.name || `Event #${e.id}`}
        </span>
        <Badge
          variant={e.status === MaintenanceEventStatus.VALIDATED ? "default" : "secondary"}
          className="text-[10px]"
        >
          {MaintenanceEventStatusLabels[e.status]}
        </Badge>
      </div>
      {e.summary && (
        <p className="text-xs text-muted-foreground line-clamp-2">{e.summary}</p>
      )}
      {e.supplierReport && (
        <p className="text-xs text-blue-600 line-clamp-2">{e.supplierReport}</p>
      )}
      {e.reporterName && (
        <p className="text-[10px] text-muted-foreground mt-1">door {e.reporterName}</p>
      )}
    </div>
  );
}
